import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BuildingLoadingOverlay } from "@/components/building/BuildingLoadingOverlay";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { BuildingView } from "@/components/building/BuildingView";
import { useScrollReset } from "@/hooks/useScrollReset";
import { useBuildingDetail } from "@/hooks/buildings/useBuildingDetail";
import { useBuildingDocumentStats } from "@/hooks/buildings/useBuildingDocumentStats";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { useBuildingStore } from "@/hooks/useBuildingStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useToast } from "@/hooks/use-toast";
import type { BuildingRow } from "@/stores/buildings/models";

const OVERLAY_MIN_MS = 600;
const OVERLAY_CAP_MS = 2000;

const BuildingDetail = observer(function BuildingDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useLanguage();
  const buildingStore = useBuildingStore();
  const { currentOrganization } = useOrganizationContext();
  const { toast } = useToast();

  // Apply scroll reset to prevent automatic scrolling
  useScrollReset();

  const { detail, isLoading, error, refetch } = useBuildingDetail(id);

  // Global building scope — when a project is selected in the header the
  // dashboard graphs narrow to that project. Threaded into BuildingView and the
  // pre-warm stats below so the overlay gates on the scoped data.
  const { selectedProjectId } = useBuildingScope(id ?? "");
  const scopeProjectId = selectedProjectId != null ? String(selectedProjectId) : undefined;

  // Mirror every docStats subquery the dashboard charts depend on so the
  // overlay only lifts once chart data is ready — react-query dedupes by
  // key, so this adds subscribers, not requests. Without this the donut /
  // area / bar charts would mount with `isLoading` text and then animate
  // when their data arrives, defeating the overlay's purpose.
  const docStats = useBuildingDocumentStats(id, {
    timeRange: "30d",
    projectId: scopeProjectId,
  });

  const isAnyDataLoading =
    isLoading ||
    docStats.isCountsLoading ||
    docStats.isByGroupLoading ||
    docStats.isUploadActivityLoading ||
    docStats.isPerProjectLoading;

  // Distinguish "everything cached & ready" from "store hasn't started
  // loading yet". `useBuildingDetail` flips `isLoading` true inside a
  // useEffect, so on the first synchronous render `isLoading` is false
  // even when nothing is cached. Requiring `detail.base` to exist closes
  // that gap — only treat as cached when the MST store actually has data.
  const isCachedAndReady = !!detail?.base && !isAnyDataLoading;

  const mountedAt = useRef(Date.now());
  // Skip the overlay entirely on cached navigations (back-button, repeat
  // visits). On a cold load this initialises true and the effect below
  // schedules the lift.
  const [showOverlay, setShowOverlay] = useState(() => !isCachedAndReady);

  useEffect(() => {
    if (!showOverlay) return;
    // Never lift while detail is still loading — `data` would be null
    // and BuildingView would briefly render its "building not found"
    // alert before real data lands.
    if (isLoading) return;

    const isReady = !isAnyDataLoading;
    const elapsed = Date.now() - mountedAt.current;
    const wait = isReady
      ? Math.max(0, OVERLAY_MIN_MS - elapsed)
      : Math.max(0, OVERLAY_CAP_MS - elapsed);
    const timeout = setTimeout(() => setShowOverlay(false), wait);
    return () => clearTimeout(timeout);
  }, [isLoading, isAnyDataLoading, showOverlay]);

  const handleBuildingChange = async (updatedFields: Partial<BuildingRow>) => {
    if (!id) return;
    await buildingStore.updateBuildingFields(id, updatedFields);
    // Store updates will propagate; refetch kept for safety
    await refetch();
  };

  const handleFieldsSave = async (fields: Record<string, string>) => {
    if (!id || !currentOrganization?.id) return;
    await buildingStore.saveUserAttributes(id, currentOrganization.id, fields);
    toast({
      title: (t.buildingDetail.dashboard as any).fieldsSaved ?? "Building details updated",
    });
    await refetch();
  };

  const viewError = error ? new Error(String(error)) : null;

  if (viewError) {
    if (process.env.NODE_ENV === "development") {
      console.error("❌ BuildingDetail error:", viewError);
    }
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTitle>{t.common.error}</AlertTitle>
          <AlertDescription>{viewError.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Map store detail to the shape expected by BuildingView
  const data = detail?.base
    ? {
        building: {
          ...detail.base,
          id: String(detail.base.id),
          created_by: detail.base.created_by ? String(detail.base.created_by) : undefined,
          org_id: String(detail.base.org_id),
        },
      }
    : null;

  return (
    <BuildingLoadingOverlay visible={showOverlay} label={t.common.loading}>
      <BuildingView
        data={data}
        isLoading={isLoading}
        error={viewError}
        onBuildingChange={handleBuildingChange}
        onFieldsSave={handleFieldsSave}
        projectId={scopeProjectId}
        t={t}
      />
    </BuildingLoadingOverlay>
  );
});
export default BuildingDetail;
